import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Paper,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchSuccessFactorsByBusinessDomain,
  fetchHumanCapitalIssues,
  fetchInfoCapitalIssuesApp,
  fetchInfoCapitalIssuesData,
  fetchInfoCapitalIssuesIt,
  fetchOrgCapitalIssues,
  createProjectGoal,
  fetchBscStrategicIssueGoals,
  fetchBscHumanCapitalGoalGoals,
  fetchBscHumanCapitalIssueGoals,
  fetchBscInfoCapitalGoalAppGoals,
  fetchBscInfoCapitalGoalDataGoals,
  fetchBscInfoCapitalGoalItGoals,
  fetchBscInfoCapitalIssueAppGoals,
  fetchBscInfoCapitalIssueDataGoals,
  fetchBscInfoCapitalIssueItGoals,
  fetchBscOrgCapitalGoalGoals,
  fetchBscOrgCapitalIssueGoals,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function ProjectGoalCreatePage() {
  const { businessDomainId, businessUnitId, projectId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}`;

  const [successFactors, setSuccessFactors] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [selectedSuccessFactorId, setSelectedSuccessFactorId] = useState("");
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [form, setForm] = useState({ plannedValue: "", plannedDate: "", deadline: "" });

  useEffect(() => {
    (async () => {
      try {
        // BSCに登録済みのSF IDを全カテゴリから収集する
        const [bscResults, extraIssues, allSfs] = await Promise.all([
          Promise.all([
            fetchBscStrategicIssueGoals(businessUnitId).catch(() => []),    // 戦略目標
            fetchBscHumanCapitalGoalGoals(businessUnitId).catch(() => []),  // 人的資本目標
            fetchBscHumanCapitalIssueGoals(businessUnitId).catch(() => []), // 人的資本課題
            fetchBscInfoCapitalGoalAppGoals(businessUnitId).catch(() => []),  // 情報資本App目標
            fetchBscInfoCapitalGoalDataGoals(businessUnitId).catch(() => []), // 情報資本Data目標
            fetchBscInfoCapitalGoalItGoals(businessUnitId).catch(() => []),   // 情報資本IT目標
            fetchBscInfoCapitalIssueAppGoals(businessUnitId).catch(() => []), // 情報資本App課題
            fetchBscInfoCapitalIssueDataGoals(businessUnitId).catch(() => []),// 情報資本Data課題
            fetchBscInfoCapitalIssueItGoals(businessUnitId).catch(() => []),  // 情報資本IT課題
            fetchBscOrgCapitalGoalGoals(businessUnitId).catch(() => []),  // 組織資本目標
            fetchBscOrgCapitalIssueGoals(businessUnitId).catch(() => []), // 組織資本課題
          ]),
          Promise.all([
            fetchHumanCapitalIssues(businessDomainId).catch(() => []),
            fetchInfoCapitalIssuesApp(businessDomainId).catch(() => []),
            fetchInfoCapitalIssuesData(businessDomainId).catch(() => []),
            fetchInfoCapitalIssuesIt(businessDomainId).catch(() => []),
            fetchOrgCapitalIssues(businessDomainId).catch(() => []),
          ]),
          fetchSuccessFactorsByBusinessDomain(businessDomainId),
        ]);
        const bscSfIds = new Set(
          bscResults.flat().map((g) => g.successFactorId).filter(Boolean)
        );
        // 全SF（通常SF + 全Issue種別）を結合してBSC登録済みのものだけにフィルタし、successFactorId で重複除去する
        const combinedSfs = [
          ...(Array.isArray(allSfs) ? allSfs : []),
          ...extraIssues.flat(),
        ];
        const seen = new Set();
        const filtered = combinedSfs.filter((sf) => {
          if (!bscSfIds.has(sf.successFactorId)) return false;
          if (seen.has(sf.successFactorId)) return false;
          seen.add(sf.successFactorId);
          return true;
        });
        setSuccessFactors(filtered);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, businessUnitId]);

  const handleSuccessFactorChange = (sfId) => {
    setSelectedSuccessFactorId(sfId);
    setSelectedIndicator(null);
    const sf = successFactors.find((s) => s.successFactorId === sfId);
    setIndicators(Array.isArray(sf?.indicators) ? sf.indicators : []);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSuccessFactorId) { notify.error(t("project.goal.successFactor") + " " + t("common.selectRequired")); return; }
    if (!selectedIndicator) { notify.error(t("project.goal.indicator") + " " + t("common.selectRequired")); return; }
    try {
      await createProjectGoal(projectId, selectedSuccessFactorId, {
        indicator: selectedIndicator,
        plannedValue: form.plannedValue,
        plannedDate: form.plannedDate || null,
        deadline: form.deadline || null,
      });
      notify.success(t("message.create.success"));
      navigate(`${basePath}/goals`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("project.goal.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("project.goal.successFactor")}</InputLabel>
              <Select value={selectedSuccessFactorId}
                onChange={(e) => handleSuccessFactorChange(e.target.value)}
                label={t("project.goal.successFactor")}>
                {successFactors.map((sf) => (
                  <MenuItem key={sf.successFactorId} value={sf.successFactorId}>
                    {sf.successFactorName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth required disabled={!selectedSuccessFactorId}>
              <InputLabel>{t("project.goal.indicator")}</InputLabel>
              <Select value={selectedIndicator?.indicatorName || ""}
                onChange={(e) => {
                  const found = indicators.find((i) => i.indicatorName === e.target.value);
                  setSelectedIndicator(found || null);
                }}
                label={t("project.goal.indicator")}>
                {indicators.map((i) => (
                  <MenuItem key={i.indicatorName} value={i.indicatorName}>
                    {i.indicatorName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedIndicator && (
              <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: "grey.50" }}>
                {[
                  { label: t("appKpi.unit"),               value: selectedIndicator.unit },
                  { label: t("appKpi.measuringMethod"),     value: selectedIndicator.measuringMethod },
                  { label: t("appKpi.evaluationStandard"),  value: selectedIndicator.evaluationStandard },
                  { label: t("appKpi.description"),         value: selectedIndicator.description },
                ].filter((row) => row.value).map((row) => (
                  <Box key={row.label} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                      {row.label}:
                    </Typography>
                    <Typography variant="body2">{row.value}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>
          <Grid size={12}>
            <TextField fullWidth required
              label={`${t("project.goal.plannedValue")}${selectedIndicator?.unit ? `（${selectedIndicator.unit}）` : ""}`}
              name="plannedValue" value={form.plannedValue} onChange={handleChange} />
          </Grid>

          <Grid size={12}>
            <TextField fullWidth type="date" label={t("project.goal.plannedDate")} name="plannedDate"
              value={form.plannedDate} onChange={handleChange} slotProps={{ inputLabel: { shrink: true } }} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth type="date" label={t("project.goal.deadline")} name="deadline"
              value={form.deadline} onChange={handleChange} slotProps={{ inputLabel: { shrink: true } }} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/goals`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
